import {
  FABRIC_SKU, POST_SKU, FITTING_SKU, CANTILEVER_SKU, GATE_HW_SKU,
  POST_OPTIONS, getPostDiameter, getTensionBarLength, getCantileverGateSKU,
  getSwingGateSize,
} from '../data/estimatorSKUs';

const lookupSKU = (sku, products) => products.find(p => p.sku === sku && p.isActive);

export function getRailDiameter(fenceClass, height) {
  if (fenceClass === 'Residential' && parseFloat(height) <= 6) return '1-3/8"';
  return '1-5/8"';
}

export function getFabricDesc(finish, fenceClass, height) {
  const h = parseFloat(height);
  const ht = h === 3.5 ? '42"' : `${h}'`;
  if (finish === 'Black') {
    if (h === 10) return `1-3/4" x 9 Gauge x 10' Black Vinyl`;
    return `2" x 9 Gauge x ${ht} Black Vinyl`;
  }
  if (fenceClass === 'Residential') {
    if (h <= 3.5) return `2-1/4" x 11-1/2 Gauge x ${ht} Galvanized`;
    return `2-3/8" x 11-1/2 Gauge x ${ht} Galvanized`;
  }
  return `2" x 9 Gauge x ${ht} Galvanized`;
}

export function calculateChainLink(specs, products, priceLevel = 'A') {
  const {
    finish, fenceClass, height,
    topRail, bottomRail, midRail, braceRail,
    barbWire, coilTop, coilBottom,
    postSpacing,
    endPostSize, cornerPostSize, linePostSize,
    runs, totalEnds, totalCorners,
    tieOnsEnabled, tieOns,
    gates,
  } = specs;

  const warnings = [];
  const items = [];
  const h = parseFloat(height);
  const spacing = parseFloat(postSpacing) || 10;
  const isBlack = finish === 'Black';
  const fp = isBlack ? 'Black' : 'Galv';

  const totalFootage = (runs || []).reduce((s, r) => s + (parseFloat(r.length) || 0), 0);
  const linePosts = (runs || []).reduce((sum, r) => {
    const ft = parseFloat(r.length) || 0;
    if (ft <= 0) return sum;
    // ceil so partial spans still add a line post (e.g. 23ft @ 10ft spacing → 2)
    return sum + Math.max(0, Math.ceil(ft / spacing) - 1);
  }, 0);
  console.log('[ESTIMATOR] Line posts:', { runs, spacing, linePosts });
  const endPosts = parseInt(totalEnds) || 0;
  const cornerPosts = parseInt(totalCorners) || 0;
  // Normalize gate post sizes — for Double Swing, the user picks ONE size that
  // applies to BOTH gate posts (the form doesn't show a separate latch post field
  // for double swing). But `gate.latchPostSize` may still be set in state with a
  // stale or default value. Force it to equal `postSize` for Double Swing so both
  // gate posts use the same SKU. Single Swing keeps the user's separate
  // hinge/latch selections.
  const normalizedGates = (gates || []).map(g => {
    if (g.type === 'Double Swing') {
      return { ...g, latchPostSize: g.postSize };
    }
    return g;
  });
  const swingGates = normalizedGates.filter(g => g.type !== 'Cantilever');
  const cantGates = normalizedGates.filter(g => g.type === 'Cantilever');
  // Total tie-on quantity (0 when tie-ons are disabled). Tie-ons attach to an
  // existing post, so they count toward brace bands and rail ends but NOT post caps.
  const tieOnCount = (tieOnsEnabled && tieOns)
    ? tieOns.reduce((sum, t) => sum + (parseInt(t.qty) || 0), 0)
    : 0;

  const endDiam = getPostDiameter(endPostSize);
  const cornerDiam = getPostDiameter(cornerPostSize);
  const lineDiam = getPostDiameter(linePostSize);

  const getPrice = (product) => {
    if (!product) return 0;
    if (priceLevel === 'B') return parseFloat(product.priceB || 0);
    if (priceLevel === 'C') return parseFloat(product.priceC || 0);
    return parseFloat(product.priceA || 0);
  };

  const addItem = (sku, qty, fallbackLabel, unit = 'pc', roundable = false) => {
    if (!sku || qty <= 0) return;
    const product = lookupSKU(sku, products);
    if (!product) warnings.push(sku);
    items.push({
      id: Date.now() + Math.random(),
      sku,
      description: product ? product.description : `⚠️ PRODUCT NOT FOUND - SKU: ${sku}`,
      qty: Math.ceil(qty),
      unit: product?.unit || unit,
      unitPrice: getPrice(product),
      isStock: product?.isStock ?? true,
      productId: product?.id || null,
      found: !!product,
      roundable,
      fromEstimator: true,
      estimatorKind: 'chainlink',
    });
  };

  const tbAccum = {};
  const bbAccum = {};
  const pcAccum = {};

  const addTB = (d, q) => { if (d && q > 0) tbAccum[d] = (tbAccum[d] || 0) + q; };
  const addBB = (d, q) => { if (d && q > 0) bbAccum[d] = (bbAccum[d] || 0) + q; };
  const addPC = (d, q) => { if (d && q > 0) pcAccum[d] = (pcAccum[d] || 0) + q; };

  // 1. Fabric
  const fabricKey = isBlack ? `Black-${height}` : `${fenceClass}-Galvanized-${height}`;
  addItem(FABRIC_SKU[fabricKey], totalFootage, `Fabric ${height}'`, 'ft', true);

  // 2. Line Posts
  if (linePosts > 0 && linePostSize) {
    addItem(POST_SKU[`${fp}-${linePostSize}`], linePosts, linePostSize);
  }

  // 3. End Posts
  if (endPosts > 0 && endPostSize) {
    addItem(POST_SKU[`${fp}-${endPostSize}`], endPosts, endPostSize);
  }

  // 4. Corner Posts
  if (cornerPosts > 0 && cornerPostSize) {
    addItem(POST_SKU[`${fp}-${cornerPostSize}`], cornerPosts, cornerPostSize);
  }

  // 4b. Swing Gate Frames (pre-built single/double swing gates) — added as their
  // own line item, separate from gate posts and hardware. Both Residential and
  // Commercial round the entered opening/height UP to the next standard size
  // (universal round-up). Cantilever gates have their own SKU logic (section 19).
  swingGates.forEach((gate, gateIndex) => {
    const { sku: gateSku, openingLabel, heightLabel, outOfRange } =
      getSwingGateSize(gate.opening, h, gate.type, fenceClass, isBlack);
    const colorDesc = isBlack ? 'Black' : 'Galv';
    console.log('[ESTIMATOR] Swing gate frame:', {
      gateIndex, opening: gate.opening, openingFt: (parseFloat(gate.opening) || 0) / 12,
      height: h, fenceClass, gateType: gate.type, color: colorDesc,
      gateSku, openingLabel, heightLabel, outOfRange,
    });
    const gateProduct = lookupSKU(gateSku, products);
    if (gateProduct) {
      items.push({
        id: Date.now() + Math.random(),
        sku: gateProduct.sku,
        description: gateProduct.description,
        qty: 1,
        unit: gateProduct.unit || 'pc',
        unitPrice: getPrice(gateProduct),
        isStock: gateProduct.isStock ?? true,
        productId: gateProduct.id,
        found: true,
        fromEstimator: true,
        estimatorKind: 'chainlink',
      });
    } else {
      warnings.push(gateSku);
      items.push({
        id: Date.now() + Math.random(),
        sku: gateSku,
        description: `⚠️ ${openingLabel} x ${heightLabel} ${gate.type} ${colorDesc} Gate (SKU ${gateSku} not in price list)`,
        qty: 1,
        unit: 'pc',
        unitPrice: 0,
        isStock: true,
        productId: null,
        found: false,
        fromEstimator: true,
        estimatorKind: 'chainlink',
      });
    }
  });

  // 5. Gate Posts
  swingGates.forEach((gate) => {
    const gs = gate.postSize;
    const ls = gate.latchPostSize || gs;
    if (gs === ls) {
      addItem(POST_SKU[`${fp}-${gs}`], 2, gs);
    } else {
      addItem(POST_SKU[`${fp}-${gs}`], 1, gs);
      addItem(POST_SKU[`${fp}-${ls}`], 1, ls);
    }
  });
  cantGates.forEach((gate) => {
    addItem(POST_SKU[`${fp}-${gate.postSize}`], 3, gate.postSize);
  });

  // 6. Rails (all types share the same SKU, combined into one item per type)
  // Footage is used as entered — gate openings are NOT subtracted.
  const railKey = POST_OPTIONS[fenceClass]?.[height]?.rail;
  const railSku = railKey ? POST_SKU[`${fp}-${railKey}`] : null;
  const railPcs = totalFootage > 0 ? Math.ceil(totalFootage / 21) : 0;
  const gatePosts = swingGates.length * 2 + cantGates.length * 3;

  // Top Rail: one continuous rail along the fence line (no waste buffer).
  if (topRail) {
    console.log('[ESTIMATOR] Top rail pipes:', { totalFootage, topRailPipes: railPcs });
    addItem(railSku, railPcs, `Top Rail ${railKey}`);
  }
  // Brace Rail: a short (~10ft) brace at each terminal post — 1 per end, 2 per
  // corner, 1 per gate post — NOT a full-length run. Shares the top-rail SKU, so
  // it merges into the same line item.
  if (braceRail) {
    const braceRailPieces = endPosts + cornerPosts * 2 + gatePosts;
    const braceRailPcs = Math.ceil((braceRailPieces * 10) / 21);
    console.log('[ESTIMATOR] Brace rail:', { endPosts, cornerPosts, gatePosts, braceRailPieces, braceRailPcs });
    addItem(railSku, braceRailPcs, `Brace Rail ${railKey}`);
  }
  if (bottomRail) addItem(railSku, railPcs, `Bottom Rail ${railKey}`);
  if (midRail)    addItem(railSku, railPcs, `Mid Rail ${railKey}`);

  // 7. Tension Bands (accumulate by diameter then emit one item per diameter)
  // Use ceil(h-1) so half-foot heights round up to a whole band count per post
  // (3.5' → ceil(2.5) = 3 bands/post; whole-foot heights are unchanged).
  const bandsPerEnd = Math.ceil(h - 1);
  addTB(endDiam, endPosts * bandsPerEnd);
  addTB(cornerDiam, cornerPosts * 2 * bandsPerEnd);
  swingGates.forEach(gate => {
    addTB(getPostDiameter(gate.postSize), bandsPerEnd);
    addTB(getPostDiameter(gate.latchPostSize || gate.postSize), bandsPerEnd);
  });
  cantGates.forEach(gate => addTB(getPostDiameter(gate.postSize), 2 * bandsPerEnd));
  // Tie-ons behave like end posts: bandsPerEnd tension bands at the tie-on diameter.
  if (tieOnsEnabled && tieOns) {
    tieOns.forEach(t => {
      const q = parseInt(t.qty) || 0;
      if (q > 0) addTB(t.diameter, q * bandsPerEnd);
    });
  }

  let totalTensionBands = 0;
  Object.entries(tbAccum).forEach(([d, q]) => {
    totalTensionBands += q;
    addItem(FITTING_SKU[`${fp}-TensionBand-${d}`], q, `Tension Band ${d}`);
  });
  console.log('[ESTIMATOR] Tension bands:', {
    h, bandsPerEnd, endPosts, cornerPosts,
    gatePosts: swingGates.length * 2 + cantGates.length * 3, totalTensionBands,
  });

  // 8. Tension Bars
  const tbarLen = getTensionBarLength(h);
  const tensionBars = endPosts + cornerPosts * 2 + swingGates.length * 2 + cantGates.length * 2;
  if (tensionBars > 0) {
    addItem(FITTING_SKU[`${fp}-TensionBar-${tbarLen}`], tensionBars, `Tension Bar ${tbarLen}"`);
  }

  // 9. Brace Bands — one per rail (including brace rail) and per coil wire at each
  // end/gate post, doubled at corners. Barb wire adds 3 per end/gate post and 6
  // per corner.
  let braceBandUnits = 0;
  if (topRail)    braceBandUnits += 1;
  if (midRail)    braceBandUnits += 1;
  if (bottomRail) braceBandUnits += 1;
  if (braceRail)  braceBandUnits += 1;
  if (coilTop)    braceBandUnits += 1;
  if (coilBottom) braceBandUnits += 1;
  if (braceBandUnits > 0) {
    addBB(endDiam, endPosts * braceBandUnits);
    addBB(cornerDiam, cornerPosts * 2 * braceBandUnits);
    swingGates.forEach(gate => {
      addBB(getPostDiameter(gate.postSize), braceBandUnits);
      addBB(getPostDiameter(gate.latchPostSize || gate.postSize), braceBandUnits);
    });
    // Tie-ons behave like end posts: one brace band per rail/coil unit, at the
    // tie-on's own diameter.
    if (tieOnsEnabled && tieOns) {
      tieOns.forEach(t => {
        const q = parseInt(t.qty) || 0;
        if (q > 0) addBB(t.diameter, q * braceBandUnits);
      });
    }
  }
  if (barbWire) {
    addBB(endDiam, endPosts * 3);
    addBB(cornerDiam, cornerPosts * 6);
    swingGates.forEach(gate => {
      addBB(getPostDiameter(gate.postSize), 3);
      addBB(getPostDiameter(gate.latchPostSize || gate.postSize), 3);
    });
    // Tie-ons behave like end posts under barb wire: 3 extra brace bands each.
    if (tieOnsEnabled && tieOns) {
      tieOns.forEach(t => {
        const q = parseInt(t.qty) || 0;
        if (q > 0) addBB(t.diameter, q * 3);
      });
    }
  }
  // Brace rail adds a brace band at the LINE-POST side of each brace (the terminal
  // side is already counted above via braceBandUnits). Sized to the line diameter.
  // One brace pipe per end/gate-post/tie-on location, doubled at corners — matching
  // the physical brace-rail piece count (section 6) plus tie-ons.
  // Only when line posts exist: with 0 line posts (runs shorter than the post
  // spacing) there is no line-post side to brace to, so these are phantom.
  if (braceRail && lineDiam && linePosts > 0) {
    const numBraceRails = endPosts + cornerPosts * 2 + gatePosts + tieOnCount;
    addBB(lineDiam, numBraceRails);
  }
  // Cantilever gate posts use a fixed brace-band count for track/bracket mounting.
  cantGates.forEach(gate => addBB(getPostDiameter(gate.postSize), 4));

  let totalBraceBands = 0;
  Object.entries(bbAccum).forEach(([d, q]) => {
    totalBraceBands += q;
    addItem(FITTING_SKU[`${fp}-BraceBand-${d}`], q, `Brace Band ${d}`);
  });
  console.log('[ESTIMATOR] Brace bands:', {
    endPosts, cornerPosts, gatePosts, tieOnCount, braceBandUnits, totalBraceBands,
  });

  // 10. Rail Ends — sized to the RAIL diameter, NOT the post diameter, so every
  // rail end is the same SKU regardless of post size (the rail-end SKU map only
  // has rail sizes: 1-3/8" and 1-5/8"). One per standard rail (top/mid/bottom) at
  // each end/gate post, doubled at corners; the brace rail adds two per brace pipe
  // (both ends). Coil wire and barb wire get none.
  let railEndUnits = 0;
  if (topRail)    railEndUnits += 1;
  if (midRail)    railEndUnits += 1;
  if (bottomRail) railEndUnits += 1;
  // braceRail is handled separately below: it has a rail end at BOTH the terminal
  // AND the line-post side of each brace pipe, so it can't share railEndUnits.
  // Rail-end locations: each end post, each swing-gate post (2/gate), each
  // cantilever gate (1), and 2 per corner post.
  // Tie-ons count as end-like rail-end locations (no corner-style doubling).
  const railEndPosts = endPosts + cornerPosts * 2 + swingGates.length * 2 + cantGates.length + tieOnCount;
  let railEndCount = railEndUnits * railEndPosts;
  // Brace rail: each brace pipe has a rail end at BOTH ends (terminal + line post),
  // counted at the physical brace-pipe count (section 6) plus tie-ons. With 0 line
  // posts there is no line-post side, so only the terminal-side end exists (×1).
  if (braceRail) {
    const numBraceRails = endPosts + cornerPosts * 2 + gatePosts + tieOnCount;
    railEndCount += numBraceRails * (linePosts > 0 ? 2 : 1);
  }
  if (railEndCount > 0) {
    const railDiam = getRailDiameter(fenceClass, height);
    const railEndSku = FITTING_SKU[`${fp}-RailEnd-${railDiam}`];
    console.log('[ESTIMATOR] Rail ends:', {
      railEndUnits, railEndPosts, tieOnCount, railEndCount, railDiam, railEndSku,
      found: !!lookupSKU(railEndSku, products),
    });
    addItem(railEndSku, railEndCount, `Rail End ${railDiam}`);
  }

  // 11. Post Caps
  addPC(endDiam, endPosts);
  addPC(cornerDiam, cornerPosts);
  swingGates.forEach(gate => {
    addPC(getPostDiameter(gate.postSize), 1);
    addPC(getPostDiameter(gate.latchPostSize || gate.postSize), 1);
  });
  cantGates.forEach(gate => addPC(getPostDiameter(gate.postSize), 3));
  Object.entries(pcAccum).forEach(([d, q]) => {
    addItem(FITTING_SKU[`${fp}-PostCap-${d}`], q, `Post Cap ${d}`);
  });

  // 12. Line post tops: Barb Arms when barb wire is present, otherwise Loop Caps.
  if (linePosts > 0 && lineDiam) {
    if (barbWire) {
      // One 3-strand standard barb arm per line post, sized to the line post diameter.
      addItem(FITTING_SKU[`${fp}-BarbArm-${lineDiam}`], linePosts, `Barb Arm ${lineDiam}`);
    } else {
      const loopKey = fenceClass === 'Residential'
        ? `${fp}-LoopCapRes-${lineDiam} Post`
        : `${fp}-LoopCap-${lineDiam} Post`;
      addItem(FITTING_SKU[loopKey], linePosts, `Loop Cap ${lineDiam}`);
    }
  }

  // 13. Boulevard Clamps (mid or bottom rail on line posts)
  if ((midRail || bottomRail) && linePosts > 0 && lineDiam) {
    const clampRails = (midRail ? 1 : 0) + (bottomRail ? 1 : 0);
    const bvdKey = fenceClass === 'Residential'
      ? `${fp}-BoulevardRes-${lineDiam}`
      : `${fp}-Boulevard-${lineDiam}`;
    addItem(FITTING_SKU[bvdKey], linePosts * clampRails, `Boulevard Clamp ${lineDiam}`);
  }

  // 14. Aluminum / Black Ties — (h-1) per line post, plus 1 every 2 ft along EACH
  // selected rail (top, mid, bottom, brace). (Commercial → Long, Residential → Short.)
  let totalTies = linePosts * (h - 1);
  if (topRail)    totalTies += Math.ceil(totalFootage / 2);
  if (midRail)    totalTies += Math.ceil(totalFootage / 2);
  if (bottomRail) totalTies += Math.ceil(totalFootage / 2);
  if (braceRail)  totalTies += Math.ceil(totalFootage / 2);
  console.log('[ESTIMATOR] Fence ties:', {
    linePosts, h, totalFootage, topRail, midRail, bottomRail, braceRail, totalTies,
  });
  if (totalTies > 0) {
    const tieLength = fenceClass === 'Commercial' ? 'Long' : 'Short';
    const tieFinish = isBlack ? 'Black' : 'Galv';
    const tieSku = FITTING_SKU[`AluminumTie-${tieLength}-${tieFinish}`];
    addItem(tieSku, totalTies, 'Aluminum Ties', 'pc', true);
  }

  // 15. Coil Wire & Hog Rings — coil wire is quantified per foot; the user presses
  // Round Up to convert to 1000' rolls. One run per selected position (top/bottom).
  if (coilTop || coilBottom) {
    const coilRuns = (coilTop ? 1 : 0) + (coilBottom ? 1 : 0);
    addItem(FITTING_SKU[`${fp}-CoilWire`], totalFootage * coilRuns, 'Coil Wire', 'ft', true);
    // ── HOG RINGS ARE ALWAYS GALVANIZED. THIS IS DELIBERATE. ──
    //
    // A BUSINESS RULE FROM THE SHOP, not an oversight and not a bug: hog rings
    // are crimped around the tension wire and end up hidden behind the fabric,
    // so the black ones buy nothing visible and stocking two kinds is not worth
    // it. A black fence gets HR9AP like every other fence.
    //
    // ⚠️ THIS APPLIES TO HOG RINGS ONLY. `fp` is very much still in use — it
    // is the finish key for the fabric, the posts, the tension and brace bands,
    // the ties, the coil wire on the line above and the barbed wire below, and
    // every one of those still emits its Black variant on a black fence. Do not
    // read this line as evidence that the finish key is dead.
    //
    // HRBK is NOT removed from estimatorSKUs.js. It is a real product, it is on
    // the price list, and it can still be added to a quote by hand through the
    // product picker. Only the ESTIMATOR stops reaching for it.
    addItem(FITTING_SKU['Galv-HogRings'], Math.ceil((totalFootage / 2) * coilRuns), 'Hog Rings');
  }

  // 16. Barbed Wire — 3 strands per linear foot of fence. Shown per-foot in the
  // estimator (footage × 3); the user presses Round Up to convert to 1320' rolls.
  // This matches how Coil Wire is handled (section 15).
  if (barbWire) {
    addItem(FITTING_SKU[`${fp}-BarbWire`], totalFootage * 3, 'Barbed Wire', 'ft', true);
  }

  // 17. Carriage Bolts — 1 per tension band, 1 per brace band, 2 per collar.
  // Collars: Residential swing gates and Commercial single-swing gates only
  // (Commercial double swing uses an ILD; cantilever has no collar).
  let collarsCount = 0;
  normalizedGates.forEach(gate => {
    const isRes = fenceClass === 'Residential';
    if (isRes && (gate.type === 'Single Swing' || gate.type === 'Double Swing')) collarsCount += 1;
    if (!isRes && gate.type === 'Single Swing') collarsCount += 1;
  });
  const carriageBolts = totalTensionBands + totalBraceBands + collarsCount * 2;
  if (carriageBolts > 0) {
    addItem(FITTING_SKU['CarriageBolt'], carriageBolts, 'Carriage Bolt 5/16"', 'pc', true);
  }

  // 18. Tie-On Fittings — tension bars only.
  // Tension bands are included in the main tension-band formula (section 7) and
  // brace bands in the main brace-band formula (section 9), since a tie-on behaves
  // like an end post. No post cap is added: a tie-on attaches to an existing post
  // that already has its cap.
  if (tieOnsEnabled && tieOns && tieOns.length > 0) {
    tieOns.forEach(tieOn => {
      const qty = parseInt(tieOn.qty) || 1;
      if (qty <= 0) return;
      addItem(FITTING_SKU[`${fp}-TensionBar-${tbarLen}`], qty, `Tension Bar (Tie-On)`);
    });
  }

  // 19. Gate Hardware
  normalizedGates.forEach((gate) => {
    const isRes = fenceClass === 'Residential';
    const gd = getPostDiameter(gate.postSize);
    const ld = getPostDiameter(gate.latchPostSize || gate.postSize);

    // Swing gate frames (residential + commercial) are emitted in section 4b via
    // getSwingGateSize; only the gate hardware is added here.

    if (gate.type === 'Single Swing') {
      if (isRes) {
        addItem(GATE_HW_SKU[`Residential-FemaleHinge-1-3/8"-${fp}`], 2, 'Female Hinge 1-3/8"');
        addItem(GATE_HW_SKU[`Residential-MaleHinge-${gd}-${fp}`], 2, `Male Hinge ${gd}`);
        addItem(GATE_HW_SKU[`Residential-ForkLatch-${ld}-${fp}`], 1, `Fork Latch ${ld}`);
        addItem(GATE_HW_SKU[`Residential-Collar-1-3/8"-${fp}`], 1, 'Collar 1-3/8"');
      } else {
        addItem(GATE_HW_SKU[`Commercial-IOHHinge-${gd}-${fp}`], 2, `IOH Hinge ${gd}`);
        addItem(GATE_HW_SKU[`Commercial-ForkLatch-${ld}-${fp}`], 1, `Fork Latch ${ld}`);
        addItem(GATE_HW_SKU[`Commercial-Collar-1-5/8"-${fp}`], 1, 'Collar 1-5/8"');
      }
    } else if (gate.type === 'Double Swing') {
      if (isRes) {
        // Female hinges: 4 for the gate (2 per leaf × 2 leaves) + 2 extra to hold
        // the drop rod = 6 total. (Commercial doubles use an ILD, no drop rod.)
        addItem(GATE_HW_SKU[`Residential-FemaleHinge-1-3/8"-${fp}`], 6, 'Female Hinge 1-3/8"');
        addItem(GATE_HW_SKU[`Residential-MaleHinge-${gd}-${fp}`], 4, `Male Hinge ${gd}`);
        addItem(GATE_HW_SKU[`Residential-ForkLatch-1-3/8"-${fp}`], 1, 'Fork Latch 1-3/8"');
        addItem(GATE_HW_SKU[`Residential-DropRod-${fp}`], 1, 'Drop Rod');
        addItem(GATE_HW_SKU[`Residential-Collar-1-3/8"-${fp}`], 1, 'Collar 1-3/8"');
      } else {
        addItem(GATE_HW_SKU[`Commercial-IOHHinge-${gd}-${fp}`], 4, `IOH Hinge ${gd}`);
        addItem(GATE_HW_SKU[`Commercial-ILD-${fp}`], 1, 'ILD Industrial Locking Device');
      }
    } else if (gate.type === 'Cantilever') {
      // Gate panel — match opening (inches → ft) and fence height to a standard
      // SKU, rounding UP to the next available size.
      const gateResult = getCantileverGateSKU(gate.opening, h);
      if (gateResult.error) {
        items.push({
          id: Date.now() + Math.random(),
          sku: '—',
          description: `⚠️ ${gateResult.error}`,
          qty: 1,
          unit: 'pc',
          unitPrice: 0,
          isStock: false,
          productId: null,
          found: false,
          fromEstimator: true,
          estimatorKind: 'chainlink',
        });
      } else {
        addItem(
          gateResult.sku, 1,
          `${gateResult.openingFt}' x ${gateResult.heightFt}' Cantilever Slide Gate Galv`,
        );
      }

      // Cantilever hardware (rollers, latch, latch receiver)
      const size = gd === '4"' ? '4"' : '3"';
      addItem(CANTILEVER_SKU[`Roller-${size}`], 4, `Cantilever Roller ${size}`);
      addItem(CANTILEVER_SKU[`Latch-${size}`], 1, `Cantilever Latch ${size}`);
      addItem(CANTILEVER_SKU['LatchReceiver'], 1, 'Cantilever Latch Receiver');
    }
  });

  // Merge items with the same SKU into one line item (e.g. end posts + corner posts of same size)
  const skuMap = new Map();
  const noSkuItems = [];
  items.forEach(item => {
    if (!item.sku || item.sku === '—') { noSkuItems.push(item); return; }
    if (skuMap.has(item.sku)) {
      skuMap.get(item.sku).qty += item.qty;
    } else {
      skuMap.set(item.sku, { ...item });
    }
  });

  return { items: [...skuMap.values(), ...noSkuItems], warnings };
}
